import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Stethoscope,
  HeartHandshake,
  Sparkles,
  ArrowRight,
  Ear,
  Baby,
  UserRound,
  Activity,
  IndianRupee,
  MapPin,
  Phone,
  Video,
  Building2,
  GraduationCap,
  HandHeart,
  CheckCircle2,
  Clock,
  Award,
} from "lucide-react";
import SEO from "../components/SEO";
import { IMG } from "../data/images";
import { MANUFACTURERS, PRODUCTS } from "../data/products";
import { FEATURED_DOCTOR as D } from "../data/featuredDoctor";

const TRUST = [
  { icon: ShieldCheck, label: "Powered by HearClear" },
  { icon: Stethoscope, label: "Expert Audiology Support" },
  { icon: Building2, label: "Doctor & Hospital Connect" },
  { icon: GraduationCap, label: "Rehabilitation Guidance" },
  { icon: HandHeart, label: "Funding & CSR Assistance" },
  { icon: MapPin, label: "India-wide Patient Support" },
];

const WHO_NEEDS = [
  { icon: Baby, title: "Child with severe / profound hearing loss", note: "Best outcomes when implanted before 18 months." },
  { icon: UserRound, title: "Adult with poor hearing aid benefit", note: "When hearing aids no longer give enough clarity." },
  { icon: Ear, title: "Single-sided deafness (SSD)", note: "Selected SSD cases benefit from CI." },
  { icon: Activity, title: "Progressive hearing loss", note: "Gradual decline despite well-fitted hearing aids." },
  { icon: ShieldCheck, title: "Post-meningitis hearing loss", note: "Early intervention prevents cochlear ossification." },
  { icon: Sparkles, title: "Congenital hearing loss", note: "Early identification & implantation enables speech." },
];

const JOURNEY = [
  { n: 1, title: "Hearing Concern", note: "Parent or patient observes hearing/speech difficulty." },
  { n: 2, title: "Hearing Test", note: "Audiometry, OAE, BERA/ABR, ASSR for diagnosis." },
  { n: 3, title: "CI Evaluation", note: "Imaging, audiology, speech assessment, candidacy review." },
  { n: 4, title: "Funding Planning", note: "ADIP, state schemes, CSR, NGO, EMI options." },
  { n: 5, title: "Surgeon Consultation", note: "Detailed planning, expectations, device discussion." },
  { n: 6, title: "Surgery", note: "Typically 2–4 hours under general anaesthesia." },
  { n: 7, title: "Switch-On / Mapping", note: "Activation 2–4 weeks later, programming sessions follow." },
  { n: 8, title: "Auditory-Verbal Therapy", note: "Structured listening and language therapy." },
  { n: 9, title: "Long-term Progress", note: "Lifelong follow-up, upgrades, reassessment." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="India's Complete Cochlear Implant Guidance Platform"
        description="India's most trusted cochlear implant knowledge, access, doctor-connect and rehabilitation platform. Check CI eligibility, find specialists, understand cost & funding. Powered by HearClear."
        canonical="https://cochlearimplantsurgery.in/"
      />

      {/* ---------------- HERO ---------------- */}
      <section data-testid="home-hero" className="relative bg-gradient-to-br from-[#F0F9FF] via-white to-[#ECFDF5] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 10% 10%, #0369A1 0, transparent 50%), radial-gradient(circle at 90% 80%, #0F766E 0, transparent 50%)",
          }}
        />
        <div className="hc-container relative py-14 md:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0F766E]/10 text-[#0F766E] text-[13px] font-semibold rounded-full mb-5">
              <Sparkles size={14} /> Powered by HearClear
            </div>
            <h1 className="font-serif font-semibold text-[2.25rem] sm:text-[2.75rem] lg:text-[3.4rem] leading-[1.08] tracking-tight text-slate-900">
              India's Complete <span className="text-[#0369A1]">Cochlear Implant</span> Guidance Platform
            </h1>
            <p className="mt-6 text-[1.05rem] md:text-[1.18rem] text-slate-700 leading-relaxed max-w-xl">
              Understand cochlear implants, check eligibility, explore funding options,
              connect with specialists, and get lifelong rehabilitation support —
              all in one trusted national platform.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                data-testid="hero-cta-eligibility"
                to="/cochlear-implant-eligibility"
                className="btn-primary"
              >
                Check Cochlear Implant Eligibility <ArrowRight size={18} className="ml-1.5" />
              </Link>
              <Link
                data-testid="hero-cta-counsellor"
                to="/register"
                className="btn-secondary"
              >
                Speak to a CI Counsellor
              </Link>
              <Link
                data-testid="hero-cta-doctor"
                to="/doctors"
                className="btn-teal"
              >
                Meet Dr. Sanjay Sachdeva
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Baby, label: "For Parents", to: "/child-cochlear-implant" },
                { icon: UserRound, label: "For Adults", to: "/adult-cochlear-implant" },
                { icon: HandHeart, label: "CSR Partners", to: "/csr" },
                { icon: Stethoscope, label: "Meet Our CI Expert", to: "/doctors" },
              ].map((m, idx) => {
                const Ico = m.icon;
                return (
                  <Link
                    key={m.label}
                    to={m.to}
                    data-testid={`hero-module-${idx}`}
                    className="bg-white border border-slate-200 rounded-xl px-3.5 py-3 hover:border-[#0369A1] hover:bg-[#F0F9FF] transition flex items-center gap-2.5 text-[14px] font-medium text-slate-700"
                  >
                    <span className="w-9 h-9 rounded-lg bg-[#F0F9FF] flex items-center justify-center text-[#0369A1]">
                      <Ico size={18} />
                    </span>
                    {m.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[5/6] rounded-3xl overflow-hidden shadow-xl">
              <img
                data-testid="hero-image"
                src={IMG.familyChild}
                alt="A family experiencing the joy of hearing — Powered by HearClear"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg border border-slate-100 px-5 py-4 max-w-[260px] hidden md:block">
              <div className="text-[11px] uppercase tracking-wider text-[#0F766E] font-semibold">
                Trusted by families across India
              </div>
              <div className="mt-2 text-slate-900 font-serif text-[1.05rem] leading-snug font-semibold">
                From hearing diagnosis to surgery & rehabilitation, in one platform.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TRUST BAR ---------------- */}
      <section data-testid="trust-bar" className="border-y border-slate-100 bg-white">
        <div className="hc-container py-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-5 gap-x-4">
          {TRUST.map((t, i) => {
            const Ico = t.icon;
            return (
              <div key={i} className="flex items-center gap-2.5 text-[14px] font-medium text-slate-700">
                <span className="w-9 h-9 rounded-lg bg-[#F0F9FF] flex items-center justify-center text-[#0369A1] shrink-0">
                  <Ico size={18} />
                </span>
                {t.label}
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- WHAT IS A CI? ---------------- */}
      <section className="hc-section">
        <div className="hc-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Cochlear implant basics</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              What is a Cochlear Implant?
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed text-[17px]">
              A cochlear implant is a small electronic medical device that bypasses damaged
              parts of the inner ear and directly stimulates the hearing nerve. It is recommended
              when hearing aids no longer provide enough benefit — typically in severe to profound
              sensorineural hearing loss.
            </p>

            {/* Infographic: HA vs CI */}
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">Hearing Aid</div>
                <div className="font-serif text-xl text-slate-900 mt-1 font-semibold">Amplifies sound</div>
                <ul className="mt-3 space-y-1.5 text-[14px] text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-slate-400 shrink-0 mt-0.5" /> Worn outside the ear</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-slate-400 shrink-0 mt-0.5" /> Makes sounds louder</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-slate-400 shrink-0 mt-0.5" /> Mild–severe hearing loss</li>
                </ul>
              </div>
              <div className="rounded-2xl border-2 border-[#0F766E]/30 p-5 bg-[#ECFDF5]">
                <div className="text-xs uppercase font-bold text-[#0F766E] tracking-wider">Cochlear Implant</div>
                <div className="font-serif text-xl text-slate-900 mt-1 font-semibold">Stimulates the hearing nerve</div>
                <ul className="mt-3 space-y-1.5 text-[14px] text-slate-800">
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#0F766E] shrink-0 mt-0.5" /> Internal implant + external processor</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#0F766E] shrink-0 mt-0.5" /> Bypasses damaged hair cells</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#0F766E] shrink-0 mt-0.5" /> Severe–profound hearing loss</li>
                </ul>
              </div>
            </div>

            <Link to="/article/hearing-aid-vs-cochlear-implant" data-testid="cta-ha-vs-ci"
              className="mt-7 inline-flex items-center gap-1.5 text-[#0369A1] font-semibold hover:text-[#0284C7]">
              Read full comparison <ArrowRight size={16} />
            </Link>
          </div>

          <div className="relative">
            <img
              src={IMG.ciSurgery}
              alt="Cochlear implant surgery — illustration"
              className="rounded-3xl shadow-md w-full"
            />
          </div>
        </div>
      </section>

      {/* ---------------- WHO NEEDS A CI ---------------- */}
      <section className="hc-section bg-slate-50">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Who is a candidate</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Who Needs a Cochlear Implant?
            </h2>
            <p className="mt-4 text-slate-700 text-[17px] leading-relaxed">
              Candidacy is decided after a clinical evaluation. The following groups commonly benefit:
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHO_NEEDS.map((w, i) => {
              const Ico = w.icon;
              return (
                <div key={i} data-testid={`candidate-card-${i}`} className="card-hc">
                  <span className="w-12 h-12 rounded-xl bg-[#F0F9FF] flex items-center justify-center text-[#0369A1] mb-4">
                    <Ico size={22} />
                  </span>
                  <div className="font-serif text-[1.1rem] text-slate-900 font-semibold leading-snug">{w.title}</div>
                  <p className="mt-2 text-slate-600 text-[14.5px] leading-relaxed">{w.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- CI JOURNEY ---------------- */}
      <section className="hc-section">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">The complete CI journey</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              From hearing concern to lifelong follow-up
            </h2>
            <p className="mt-4 text-slate-700 text-[17px] leading-relaxed">
              A cochlear implant journey is more than surgery — it's a structured path with families,
              audiologists, surgeons and therapists working together.
            </p>
          </div>

          <div className="mt-10 relative">
            <div className="hidden lg:block absolute top-7 left-0 right-0 h-0.5 bg-gradient-to-r from-[#0369A1]/30 via-[#0F766E]/40 to-[#0369A1]/30" />
            <div className="grid lg:grid-cols-9 grid-cols-1 gap-5">
              {JOURNEY.map((j, i) => (
                <div key={i} data-testid={`journey-step-${j.n}`} className="relative bg-white border border-slate-200 rounded-2xl p-4 hover:border-[#0369A1] transition-colors">
                  <div className="w-14 h-14 -mt-9 mx-auto rounded-full bg-gradient-to-br from-[#0369A1] to-[#0F766E] text-white font-bold flex items-center justify-center text-lg shadow-md border-4 border-white">
                    {j.n}
                  </div>
                  <div className="mt-3 font-serif text-[1rem] font-semibold text-slate-900 text-center leading-tight">{j.title}</div>
                  <div className="mt-1.5 text-[12.5px] text-slate-600 text-center leading-relaxed">{j.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- ELIGIBILITY CTA ---------------- */}
      <section className="hc-section bg-gradient-to-br from-[#0369A1] to-[#0F766E] text-white">
        <div className="hc-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur text-white text-[13px] font-semibold rounded-full mb-5">
              <Sparkles size={14} /> Free Assessment
            </div>
            <h2 className="font-serif text-3xl md:text-[2.5rem] font-semibold leading-tight">
              Not sure if cochlear implant is right for you or your child?
            </h2>
            <p className="mt-5 text-[1.08rem] text-sky-50 leading-relaxed max-w-xl">
              Take our free, confidential eligibility assessment. Upload audiograms or reports
              and receive guidance from HearClear's clinical team.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                data-testid="eligibility-section-cta"
                to="/cochlear-implant-eligibility"
                className="bg-white text-[#0369A1] hover:bg-sky-50 font-semibold rounded-xl px-7 py-3.5 inline-flex items-center gap-1.5"
              >
                Start Eligibility Assessment <ArrowRight size={18} />
              </Link>
              <a
                data-testid="eligibility-call-cta"
                href="tel:+919000000000"
                className="border-2 border-white/40 hover:bg-white/10 text-white rounded-xl px-7 py-3.5 inline-flex items-center gap-2 font-medium"
              >
                <Phone size={18} /> Speak to counsellor
              </a>
            </div>
            <p className="mt-6 text-[13px] text-sky-100/80">
              This is not a diagnosis. Final candidacy is determined after clinical evaluation.
            </p>
          </div>
          <div className="hidden lg:block">
            <img src={IMG.audiologist} alt="Audiologist with patient" className="rounded-3xl shadow-2xl" />
          </div>
        </div>
      </section>

      {/* ---------------- COST + HOSPITAL ---------------- */}
      <section className="hc-section">
        <div className="hc-container grid md:grid-cols-2 gap-5">
          <Link to="/cochlear-implant-cost-funding" data-testid="quick-cost" className="card-hc">
            <span className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 mb-4">
              <IndianRupee size={22} />
            </span>
            <div className="font-serif text-xl text-slate-900 font-semibold">Cost & Funding</div>
            <p className="mt-2 text-slate-600 text-[15px]">
              Implant cost in India ranges ₹5.5L – ₹14L. ADIP, state schemes and CSR can significantly reduce cost.
            </p>
            <div className="mt-4 text-[#0369A1] font-semibold text-sm inline-flex items-center gap-1">Check funding options <ArrowRight size={14} /></div>
          </Link>

          <Link to="/doctors" data-testid="quick-doctor" className="card-hc">
            <span className="w-12 h-12 rounded-xl bg-[#ECFDF5] flex items-center justify-center text-[#0F766E] mb-4">
              <Stethoscope size={22} />
            </span>
            <div className="font-serif text-xl text-slate-900 font-semibold">Meet Dr. Sanjay Sachdeva</div>
            <p className="mt-2 text-slate-600 text-[15px]">
              35+ years in ENT & cochlear implant surgery. Principal Director — Max Healthcare, Delhi.
            </p>
            <div className="mt-4 text-[#0369A1] font-semibold text-sm inline-flex items-center gap-1">View profile & videos <ArrowRight size={14} /></div>
          </Link>
        </div>
      </section>

      {/* ---------------- TECHNOLOGY (MANUFACTURERS) ---------------- */}
      <section className="hc-section bg-slate-50" data-testid="tech-section">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Technology & Products</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              The three major cochlear implant manufacturers in India
            </h2>
            <p className="mt-4 text-slate-700 text-[17px] leading-relaxed">
              We maintain a neutral, education-first position. Genuine product images and information
              shown here are sourced from the official manufacturer sites.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {MANUFACTURERS.map((m) => {
              const ps = PRODUCTS.filter((p) => p.manufacturer === m.id);
              return (
                <Link
                  key={m.id}
                  to="/technology"
                  data-testid={`manufacturer-${m.id}`}
                  className="card-hc"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-serif text-2xl font-semibold text-slate-900">{m.name}</div>
                      <div className="text-[13px] text-slate-500 mt-0.5">{m.country}</div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: m.colorAccent + "20", color: "#1f2937" }}>
                      {ps.length} products
                    </span>
                  </div>
                  <p className="mt-3 text-slate-600 text-[14.5px] leading-relaxed">{m.description}</p>
                  <div className="mt-4 flex gap-2 overflow-hidden">
                    {ps.slice(0, 3).map((p) => (
                      <div key={p.id} className="w-16 h-16 bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <img src={p.image} alt={p.name} className="w-full h-full object-contain p-1" loading="lazy" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-[#0369A1] font-semibold text-sm inline-flex items-center gap-1">
                    Explore products <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- PATIENT STORIES TEASE ---------------- */}
      <section className="hc-section">
        <div className="hc-container">
          <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Patient stories</div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
                Real journeys. Real hearing.
              </h2>
            </div>
            <Link to="/patient-stories" data-testid="stories-all-link" className="text-[#0369A1] font-semibold hover:text-[#0284C7] inline-flex items-center gap-1">
              All stories <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="card-hc">
              <img src={IMG.familyChild} alt="Child CI journey" className="rounded-xl w-full aspect-[4/3] object-cover" />
              <div className="mt-4 text-[12px] text-[#0F766E] font-semibold uppercase tracking-wider">Child · Pune</div>
              <div className="mt-1 font-serif text-xl text-slate-900 font-semibold">"Hearing him say 'Mumma' for the first time..."</div>
              <p className="mt-2 text-slate-600 text-[15px]">Diagnosed at 12 months, implanted at age 2. Today Aarav speaks in sentences.</p>
            </div>
            <div className="card-hc">
              <img src={IMG.elderlyCouple} alt="Adult CI journey" className="rounded-xl w-full aspect-[4/3] object-cover" />
              <div className="mt-4 text-[12px] text-[#0F766E] font-semibold uppercase tracking-wider">Adult · Bangalore</div>
              <div className="mt-1 font-serif text-xl text-slate-900 font-semibold">"I never thought I'd hear my granddaughter sing again."</div>
              <p className="mt-2 text-slate-600 text-[15px]">After 8 years of progressive loss, Mrs. Devika is back to phone calls and bhajans.</p>
            </div>
            <div className="card-hc">
              <img src={IMG.familyChild2} alt="Child CI with CSR support" className="rounded-xl w-full aspect-[4/3] object-cover" />
              <div className="mt-4 text-[12px] text-[#0F766E] font-semibold uppercase tracking-wider">Child · Lucknow</div>
              <div className="mt-1 font-serif text-xl text-slate-900 font-semibold">"We did not know help was possible."</div>
              <p className="mt-2 text-slate-600 text-[15px]">Ishaan received his CI at age 3 with CSR funding. Now in regular school.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED DOCTOR ---------------- */}
      <section className="hc-section bg-slate-50" data-testid="home-featured-doctor">
        <div className="hc-container">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/5] bg-slate-100 max-w-md mx-auto">
                <img
                  src={D.photo}
                  alt={D.photoAlt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-3 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0F766E]/10 text-[#0F766E] text-[13px] font-semibold rounded-full mb-4">
                <Sparkles size={14} /> Meet our CI Expert
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
                {D.name}
              </h2>
              <div className="mt-2 text-[#0369A1] font-semibold text-[1.05rem]">{D.title}</div>
              <div className="mt-1 text-slate-500 text-[15px]">{D.position}</div>

              <p className="mt-5 text-slate-700 leading-relaxed text-[17px]">{D.bio[0]}</p>

              <div className="mt-6 flex flex-wrap gap-2">
                <span className="chip bg-white border border-slate-200 text-slate-700">
                  <Clock size={13} /> {D.years_experience}
                </span>
                <span className="chip bg-white border border-slate-200 text-slate-700">
                  <MapPin size={13} /> New Delhi
                </span>
                <span className="chip bg-white border border-slate-200 text-slate-700">
                  <Award size={13} /> Cochlear Implant · Skull Base · ENT
                </span>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link data-testid="home-doctor-profile" to="/doctors" className="btn-primary">
                  View full profile & videos <ArrowRight size={18} className="ml-1.5" />
                </Link>
                <Link data-testid="home-doctor-consult" to="/register" className="btn-secondary">
                  Request consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- REHAB ---------------- */}
      <section className="hc-section bg-slate-50">
        <div className="hc-container grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img src={IMG.audiologist2} alt="Auditory verbal therapy session" className="rounded-3xl shadow-md w-full" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Rehabilitation matters</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Surgery is only one part of the journey.
            </h2>
            <p className="mt-5 text-slate-700 text-[17px] leading-relaxed">
              The success of a cochlear implant depends on mapping, auditory training, speech
              therapy, family involvement, and consistent follow-up. Children need 12–24 months
              of structured therapy to develop spoken language after switch-on.
            </p>
            <Link
              data-testid="rehab-cta"
              to="/rehabilitation"
              className="btn-secondary mt-7"
            >
              Find Rehabilitation Support <ArrowRight size={18} className="ml-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- CSR ---------------- */}
      <section className="hc-section">
        <div className="hc-container">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-3xl p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[#B45309] uppercase tracking-wider text-[12px] font-bold mb-3">CSR Partnerships</div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
                Help a Child Hear.
              </h2>
              <p className="mt-5 text-slate-700 text-[17px] leading-relaxed">
                Corporates, NGOs, foundations and trusts can sponsor cochlear implant surgery and
                rehabilitation for children from underserved families. HearClear runs structured
                verification, surgery coordination and outcome reporting.
              </p>
              <Link
                data-testid="csr-cta"
                to="/csr"
                className="btn-accent mt-7"
              >
                Partner for CSR <ArrowRight size={18} className="ml-1.5" />
              </Link>
            </div>
            <img src={IMG.familyChild2} alt="Child supported by CSR" className="rounded-2xl shadow-md w-full aspect-[4/3] object-cover" />
          </div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="hc-section">
        <div className="hc-container text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
            Still have questions?
          </h2>
          <p className="mt-5 text-slate-700 text-[17px] leading-relaxed">
            Talk to a HearClear cochlear implant counsellor — at no cost. We'll guide you through
            tests, candidacy, funding options and connect you to specialists near you.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="btn-primary" data-testid="final-cta-register">
              Talk to a HearClear Counsellor <ArrowRight size={18} className="ml-1.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
